import type { AudioAsset } from "@aybuben/core/content/assets.ts";
import { sha256 } from "@aybuben/core/content/bundle.ts";

import { DownloadError, type AudioBody } from "./types.ts";

/** Whether a served Content-Type is the one the manifest promised. Compared on
    the bare type, not by string equality: a response legitimately carries
    parameters (`audio/mpeg; charset=binary`), and casing is not significant. */
const mimeMatches = (actual: string | null, expected: string): boolean => {
	if (actual === null) return false;
	// Safe: split with a limit of 1 always returns at least one element.
	const bare = (s: string): string => s.split(";", 1)[0]!.trim().toLowerCase();
	return bare(actual) === bare(expected);
};

export async function verifyAudioBytes(
	a: AudioAsset,
	bytes: Uint8Array,
	contentType: string | null = null,
): Promise<void> {
	// If there's no digest, this is a bundled asset — skip all verification.
	// Development must not require a manifest.
	if (a.sha256 === null) return;
	if (a.size !== null && bytes.length !== a.size) {
		throw new Error(
			`Audio ${a.fileId} size mismatch: ${bytes.length} bytes, expected size ${a.size}`,
		);
	}
	// The manifest's MIME type is a claim the publisher made about the artifact.
	// Checking it costs one string compare and catches a wrong file uploaded under
	// an audio name — the case where size and digest both pass but the bytes are
	// not playable audio.
	if (a.mimeType !== null && !mimeMatches(contentType, a.mimeType)) {
		throw new Error(
			`Audio ${a.fileId} content-type mismatch: served as ${contentType ?? "no content-type"}, expected ${a.mimeType}`,
		);
	}
	const actual = await sha256(bytes);
	if (actual !== a.sha256) {
		throw new Error(`Audio ${a.fileId} digest mismatch: expected ${a.sha256}, got ${actual}`);
	}
}

export type FetchAudio = (url: string, signal?: AbortSignal) => Promise<Response>;

async function fetchBytes(
	a: AudioAsset,
	fetchFn: FetchAudio,
	timeoutMs: number | undefined,
): Promise<{ bytes: Uint8Array<ArrayBuffer>; served: string | null }> {
	const ctl = timeoutMs === undefined ? null : new AbortController();
	const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
	try {
		const response = await fetchFn(a.url, ctl?.signal);
		if (!response.ok) {
			const kind = response.status === 429 || response.status >= 500 ? "server" : "http";
			throw new DownloadError(kind, `Audio ${a.fileId}: HTTP ${response.status}`);
		}
		const bytes = new Uint8Array(await response.arrayBuffer());
		return { bytes, served: response.headers.get("content-type") };
	} catch (e) {
		if (e instanceof DownloadError) throw e;
		// A rejected fetch, a timeout abort, a body cut off mid-read: each says
		// something about the connection and nothing about this file.
		throw new DownloadError("network", `Audio ${a.fileId}: ${String(e)}`);
	} finally {
		if (timer) clearTimeout(timer);
	}
}

/**
 * The only way from the network into the audio cache. The player and the
 * prefetch share it, so both trust exactly the same checks.
 */
export async function downloadAudio(
	a: AudioAsset,
	fetchFn: FetchAudio,
	timeoutMs?: number,
): Promise<AudioBody> {
	const { bytes, served } = await fetchBytes(a, fetchFn, timeoutMs);
	try {
		await verifyAudioBytes(a, bytes, served);
	} catch (e) {
		throw new DownloadError("integrity", e instanceof Error ? e.message : String(e));
	}
	return { bytes, type: served ?? "audio/mpeg" };
}
